import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, Checkbox, Button, Avatar, message, Upload, Tag } from "antd";
import { Camera, X, Bell } from "lucide-react";
import type { RcFile } from "antd/es/upload/interface";
import { useGetContacts } from "../../hooks/queries/contact";
import { CreateChatRoomBody } from "../../types/chat";
import { Contact } from "../../types/contact";
import { useVirtualScroll } from "../../hooks/useVirtualScroll";
import SearchInput from "../SearchInput";
import { getAvatarColor, getInitials } from "../../utils";
import styles from "./CreateGroupModal.module.scss";
import { useUploadFileMutation } from "../../hooks/mutations/use-upload-file";
import { generateGUID } from "../../../document/util";
import { useCreateChatRoom, useAddMemberInRoom } from "../../hooks/mutations/chat";

interface CreateGroupModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (roomId: string) => void;
}

const ITEM_HEIGHT = 72; // Chiều cao mỗi contact item
const CONTAINER_HEIGHT = 450; // Chiều cao container danh sách
const VISIBLE_MEMBERS_LIMIT = 2; // Số lượng members hiển thị mặc định

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ open, onCancel, onSuccess }) => {
  const [groupName, setGroupName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Map<string, Contact>>(new Map());
  const [isNotificationChannel, setIsNotificationChannel] = useState(false);
  const [avatarFile, setAvatarFile] = useState<RcFile | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [isSelectedExpanded, setIsSelectedExpanded] = useState(false);

  // Hooks
  // Hooks
  const { data: contactsData, isLoading } = useGetContacts({ keyword: debouncedSearch });
  const { uploadFilesAsync } = useUploadFileMutation();
  const { mutateAsync: createChatRoom } = useCreateChatRoom();
  const { mutateAsync: addMemberInRoom } = useAddMemberInRoom();

  const contacts = useMemo(() => {
    return contactsData?.pages.flatMap((page) => page.Data) || [];
  }, [contactsData]);

  // Virtual scroll hook
  const { virtualItems, totalHeight, scrollElementRef, handleScroll } = useVirtualScroll({
    itemCount: contacts.length,
    itemHeight: ITEM_HEIGHT,
    containerHeight: CONTAINER_HEIGHT,
    overscan: 5,
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  // Reset form chỉ khi modal mở (không reset khi đóng)
  useEffect(() => {
    if (open) {
      // Reset form khi modal mở
      setGroupName("");
      setSearchKeyword("");
      setSelectedMembers(new Map());
      setIsNotificationChannel(false);
      setAvatarFile(null);
      setAvatarPreview("");
      setIsCreating(false);
      setIsSelectedExpanded(false);
    }
  }, [open]);

  // Handle avatar upload
  const handleAvatarChange = (file: RcFile) => {
    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  // Handle member selection
  const handleMemberToggle = (contact: Contact) => {
    const newSelected = new Map(selectedMembers);
    if (newSelected.has(contact.Id)) {
      newSelected.delete(contact.Id);
    } else {
      newSelected.set(contact.Id, contact);
    }
    setSelectedMembers(newSelected);
  };

  // Get selected contacts for display
  const selectedContacts = useMemo(() => {
    return Array.from(selectedMembers.values());
  }, [selectedMembers]);

  // Get visible selected contacts based on expand state
  const visibleSelectedContacts = useMemo(() => {
    if (isSelectedExpanded || selectedContacts.length <= VISIBLE_MEMBERS_LIMIT) {
      return selectedContacts;
    }
    return selectedContacts.slice(0, VISIBLE_MEMBERS_LIMIT);
  }, [selectedContacts, isSelectedExpanded]);

  // Validate form
  const isValid = useMemo(() => {
    return groupName.trim().length > 0 && selectedMembers.size >= 2;
  }, [groupName, selectedMembers]);

  // Handle create group
  const handleCreateGroup = async () => {
    if (!isValid) {
      if (groupName.trim().length === 0) {
        message.error("Vui lòng nhập tên nhóm!");
      } else if (selectedMembers.size < 2) {
        message.error("Vui lòng chọn ít nhất 2 thành viên!");
      }
      return;
    }

    setIsCreating(true);

    try {
      // Upload avatar nếu có
      let avatarUrl = "";
      if (avatarFile) {
        const uploadResult = await uploadFilesAsync([avatarFile]);
        avatarUrl = uploadResult[0];
      }

      // Tạo payload
      const body: CreateChatRoomBody = {
        pID: generateGUID(),
        roomName: groupName.trim(),
        roomType: isNotificationChannel ? "C" : "G",
        avatarUrl: avatarUrl || undefined,
      };

      // Bước 1: Tạo nhóm
      console.log("Creating room...", body);
      const result = await createChatRoom(body);
      const roomId = result?.Data?.RoomId;

      console.log("Room created, roomId:", roomId);

      if (!roomId) {
        throw new Error("Không thể lấy RoomId từ kết quả tạo nhóm");
      }

      // Bước 2: Thêm thành viên vào nhóm
      if (selectedMembers.size > 0) {
        const participants = Array.from(selectedMembers.keys());
        console.log("Adding members to room:", participants);
        await addMemberInRoom({
          roomId,
          participants,
        });
        console.log("Members added successfully");
      }

      message.success(isNotificationChannel ? "Tạo kênh thông báo thành công!" : "Tạo nhóm thành công!");

      // Reset loading state
      setIsCreating(false);

      // Gọi callback để parent component xử lý đóng modal VÀ navigation
      // KHÔNG gọi onCancel() ở đây để tránh double state update
      if (onSuccess) {
        console.log("Calling onSuccess with roomId:", roomId);
        onSuccess(roomId);
      }
    } catch (error: any) {
      console.error("Error creating group:", error);
      message.error(error?.message || "Có lỗi xảy ra khi tạo nhóm");
      setIsCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closeIcon={<X size={20} />}
      width={550}
      className={styles.createGroupModal}
      title="Tạo nhóm mới"
      destroyOnClose
      maskClosable={false}
    >
      <div className={styles.modalContent}>
        {/* Avatar Upload */}
        <div className={styles.avatarSection}>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleAvatarChange}
            className={styles.avatarUpload}
          >
            <div className={styles.avatarBox}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" className={styles.avatarPreview} />
              ) : (
                <Camera size={32} className={styles.cameraIcon} />
              )}
            </div>
          </Upload>

          {/* Group Name Input */}
          <Input
            placeholder="Đặt tên nhóm..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className={styles.groupNameInput}
            maxLength={100}
          />
        </div>

        {/* Search Members */}
        <div className={styles.searchSection}>
          <SearchInput
            placeholder="Tìm kiếm thành viên..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>

        {/* Selected Members */}
        {selectedContacts.length > 0 && (
          <div className={styles.selectedMembersSection}>
            <div className={styles.selectedMembersHeader}>
              Đã chọn ({selectedContacts.length})
              {selectedContacts.length > VISIBLE_MEMBERS_LIMIT && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setIsSelectedExpanded(!isSelectedExpanded)}
                  className={styles.expandButton}
                >
                  {isSelectedExpanded ? "Thu gọn" : `Xem thêm ${selectedContacts.length - VISIBLE_MEMBERS_LIMIT}`}
                </Button>
              )}
            </div>
            <div className={styles.selectedMembersList}>
              {visibleSelectedContacts.map((contact) => (
                <Tag
                  key={contact.Id}
                  closable
                  onClose={() => handleMemberToggle(contact)}
                  className={styles.selectedMemberTag}
                >
                  <Avatar
                    size={24}
                    src={contact.AvatarUrl}
                    style={{
                      backgroundColor: contact.AvatarUrl ? undefined : getAvatarColor(contact.ContactName),
                      marginRight: 8,
                    }}
                  >
                    {!contact.AvatarUrl && getInitials(contact.ContactName)}
                  </Avatar>
                  {contact.ContactName}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Member List with Virtual Scroll */}
        <div
          ref={scrollElementRef}
          onScroll={handleScroll}
          className={styles.memberList}
          style={{
            height: CONTAINER_HEIGHT,
            overflow: "auto",
          }}
        >
          {isLoading ? (
            <div className={styles.loading}>Đang tải...</div>
          ) : contacts.length === 0 ? (
            <div className={styles.emptyState}>Không tìm thấy thành viên</div>
          ) : (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualItem) => {
                const contact = contacts[virtualItem.index];
                return (
                  <div
                    key={contact.Id}
                    className={styles.memberItem}
                    onClick={() => handleMemberToggle(contact)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: virtualItem.size,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <Avatar
                      size={48}
                      src={contact.AvatarUrl}
                      style={{
                        backgroundColor: contact.AvatarUrl ? undefined : getAvatarColor(contact.ContactName),
                        flexShrink: 0,
                      }}
                    >
                      {!contact.AvatarUrl && getInitials(contact.ContactName)}
                    </Avatar>
                    <div className={styles.memberInfo}>
                      <div className={styles.memberName}>{contact.ContactName}</div>
                      <div className={styles.memberDescription}>
                        {contact.Description || contact.Email || contact.CUI}
                      </div>
                    </div>
                    <Checkbox
                      checked={selectedMembers.has(contact.Id)}
                      onChange={() => handleMemberToggle(contact)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <Checkbox checked={isNotificationChannel} onChange={(e) => setIsNotificationChannel(e.target.checked)}>
            <Bell size={16} style={{ marginRight: 4, verticalAlign: "middle" }} />
            Kênh thông báo
          </Checkbox>

          <div className={styles.footerButtons}>
            <Button onClick={onCancel} className={styles.cancelBtn}>
              Huỷ
            </Button>
            <Button
              type="primary"
              onClick={handleCreateGroup}
              loading={isCreating}
              disabled={!isValid}
              className={styles.createBtn}
            >
              Tạo nhóm
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateGroupModal;
